import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Tooltip, Modal, Input, message } from "antd";
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import AiTextEnhanceControl from "./ai/AiTextEnhanceControl";
import { sanitizeHtml, isSafeHref } from "../utils/textUtils";

function unwrapAnchor(anchor) {
  const parent = anchor.parentNode;
  if (!parent) return;
  while (anchor.firstChild) {
    parent.insertBefore(anchor.firstChild, anchor);
  }
  parent.removeChild(anchor);
}

function normalizeUrlForLink(input) {
  const t = (input || "").trim();
  if (!t) return "";
  if (/^https?:/i.test(t)) return t;
  return `https://${t}`;
}

const SimpleRichTextEditor = ({
  value,
  onChange,
  placeholder,
  showAiEnhance = true,
  aiEnhanceTarget = "question",
}) => {
  const contentEditableRef = useRef(null);
  const linkEditAnchorRef = useRef(null);
  /** Same as bubble's anchor; not cleared until bubble closes (unlike linkEditAnchorRef in some paths). */
  const linkBubbleAnchorRef = useRef(null);
  /** Anchor being edited via "Change" — survives closeLinkBubble / bubble dismiss while modal is open. */
  const editingLinkAnchorRef = useRef(null);
  /** Original href when "Change" opened — used to re-find <a> if value sync replaced innerHTML. */
  const editingLinkHrefRef = useRef("");
  const linkModalModeRef = useRef("insert");
  /** Cloned range for "insert link" when modal/toolbar would clear the live selection. */
  const pendingLinkRangeRef = useRef(null);
  const hideBubbleTimeoutRef = useRef(null);
  const linkHoverThrottleRef = useRef(0);
  const linkBubbleOpenRef = useRef(false);
  const [activeFormats, setActiveFormats] = useState({});
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkModalMode, setLinkModalMode] = useState("insert");
  const [linkUrlDraft, setLinkUrlDraft] = useState("");
  const [linkBubble, setLinkBubble] = useState({
    open: false,
    x: 0,
    y: 0,
    href: "",
    anchor: null,
  });

  useEffect(() => {
    linkModalModeRef.current = linkModalMode;
  }, [linkModalMode]);

  const closeLinkBubble = useCallback(() => {
    if (hideBubbleTimeoutRef.current) {
      clearTimeout(hideBubbleTimeoutRef.current);
      hideBubbleTimeoutRef.current = null;
    }
    setLinkBubble({ open: false, x: 0, y: 0, href: "", anchor: null });
    linkEditAnchorRef.current = null;
    linkBubbleAnchorRef.current = null;
  }, []);

  const cancelHideBubble = useCallback(() => {
    if (hideBubbleTimeoutRef.current) {
      clearTimeout(hideBubbleTimeoutRef.current);
      hideBubbleTimeoutRef.current = null;
    }
  }, []);

  const scheduleHideBubble = useCallback(() => {
    cancelHideBubble();
    hideBubbleTimeoutRef.current = setTimeout(() => {
      hideBubbleTimeoutRef.current = null;
      closeLinkBubble();
    }, 220);
  }, [cancelHideBubble, closeLinkBubble]);

  const openLinkBubbleForAnchor = useCallback((anchor) => {
    if (!anchor) return;
    cancelHideBubble();
    linkEditAnchorRef.current = anchor;
    linkBubbleAnchorRef.current = anchor;
    const rect = anchor.getBoundingClientRect();
    setLinkBubble({
      open: true,
      x: Math.max(8, Math.min(rect.left, window.innerWidth - 232)),
      y: rect.bottom + 6,
      href: anchor.getAttribute("href") || "",
      anchor,
    });
  }, [cancelHideBubble]);

  useEffect(() => {
    if (!linkBubble.open || !linkBubble.anchor) return undefined;
    const anchor = linkBubble.anchor;
    const updatePosition = () => {
      if (!anchor.isConnected) {
        closeLinkBubble();
        return;
      }
      const rect = anchor.getBoundingClientRect();
      setLinkBubble((prev) =>
        prev.open && prev.anchor === anchor
          ? {
              ...prev,
              x: Math.max(8, Math.min(rect.left, window.innerWidth - 232)),
              y: rect.bottom + 6,
            }
          : prev,
      );
    };
    const el = contentEditableRef.current;
    el?.addEventListener("scroll", updatePosition, { passive: true });
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      el?.removeEventListener("scroll", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [linkBubble.open, linkBubble.anchor, closeLinkBubble]);

  useEffect(() => {
    linkBubbleOpenRef.current = linkBubble.open;
  }, [linkBubble.open]);

  useEffect(
    () => () => {
      if (hideBubbleTimeoutRef.current) {
        clearTimeout(hideBubbleTimeoutRef.current);
        hideBubbleTimeoutRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!linkBubble.open) return undefined;
    const onDocMouseDown = (e) => {
      if (e.target.closest?.("[data-link-bubble]")) return;
      const el = contentEditableRef.current;
      const a = e.target.closest?.("a");
      if (a && el?.contains(a)) return;
      closeLinkBubble();
    };
    document.addEventListener("mousedown", onDocMouseDown, true);
    return () => document.removeEventListener("mousedown", onDocMouseDown, true);
  }, [linkBubble.open, closeLinkBubble]);

  useEffect(() => {
    if (linkModalOpen) return;
    const el = contentEditableRef.current;
    if (!el) return;
    // Skip innerHTML replacement while the editor is focused to preserve cursor
    if (document.activeElement === el) return;
    const raw = value === undefined || value === null ? "" : String(value);
    const safe = sanitizeHtml(raw);
    if (el.innerHTML !== safe) {
      el.innerHTML = safe;
    }
    if (raw !== safe && onChange) {
      onChange(safe);
    }
  }, [value, onChange, linkModalOpen]);

  const checkActiveFormats = () => {
    setActiveFormats({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      insertUnorderedList: document.queryCommandState("insertUnorderedList"),
      insertOrderedList: document.queryCommandState("insertOrderedList"),
      justifyLeft: document.queryCommandState("justifyLeft"),
      justifyCenter: document.queryCommandState("justifyCenter"),
      justifyRight: document.queryCommandState("justifyRight"),
    });
  };

  const strengthenAnchorsInEditor = () => {
    const root = contentEditableRef.current;
    if (!root) return;
    root.querySelectorAll("a[href]").forEach((a) => {
      if (isSafeHref(a.getAttribute("href"))) {
        a.setAttribute("target", "_blank");
        a.setAttribute("rel", "noopener noreferrer");
      }
    });
  };

  const applySanitizedEditorHtml = useCallback(
    (el) => {
      if (!el) return;

      const sel = window.getSelection();
      const hasFocus = document.activeElement === el;
      const MARKER = "\uE000"; // Use a Private Use Area character as a temporary marker
      let hasMarker = false;

      // 1. Capture and mark selection if editor has focus
      if (hasFocus && sel && sel.rangeCount > 0 && el.contains(sel.anchorNode)) {
        try {
          const range = sel.getRangeAt(0);
          const markerNode = document.createTextNode(MARKER);
          range.insertNode(markerNode);
          hasMarker = true;
        } catch (err) {
          console.warn("SimpleRichTextEditor: Failed to insert cursor marker", err);
        }
      }

      const raw = el.innerHTML;
      const clean = sanitizeHtml(raw);

      if (clean !== raw) {
        el.innerHTML = clean;

        // 2. Restore selection by finding the marker in the sanitized HTML
        if (hasMarker) {
          const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
          let node;
          while ((node = walker.nextNode())) {
            const idx = node.textContent.indexOf(MARKER);
            if (idx !== -1) {
              const range = document.createRange();
              // Remove marker from text content immediately
              node.textContent = node.textContent.replace(MARKER, "");
              range.setStart(node, idx);
              range.collapse(true);
              sel.removeAllRanges();
              sel.addRange(range);
              break;
            }
          }
        }
      } else if (hasMarker) {
        // 3. If HTML didn't change enough to replace innerHTML, still remove the marker from live DOM
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
        let node;
        while ((node = walker.nextNode())) {
          const idx = node.textContent.indexOf(MARKER);
          if (idx !== -1) {
            node.textContent = node.textContent.replace(MARKER, "");
            break;
          }
        }
      }

      strengthenAnchorsInEditor();
      if (onChange) onChange(el.innerHTML);
    },
    [onChange],
  );

  const handleInput = (e) => {
    const el = e.currentTarget;
    // Don't call applySanitizedEditorHtml(el) which resets innerHTML and can cause cursor jumps.
    // Instead, notify parent of sanitized change while keeping the local editor state fluid.
    strengthenAnchorsInEditor();
    if (onChange) onChange(sanitizeHtml(el.innerHTML));
    checkActiveFormats();
  };

  const execCommand = (command, valueArg = null) => {
    document.execCommand(command, false, valueArg);
    const el = contentEditableRef.current;
    if (el) {
      el.focus();
      applySanitizedEditorHtml(el);
    }
    checkActiveFormats();
  };

  const capturePendingLinkRange = useCallback(() => {
    pendingLinkRangeRef.current = null;
    const el = contentEditableRef.current;
    if (!el) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    let r;
    try {
      r = sel.getRangeAt(0);
    } catch {
      return;
    }
    if (!el.contains(r.commonAncestorContainer)) return;
    if (r.collapsed) return;
    try {
      pendingLinkRangeRef.current = r.cloneRange();
    } catch {
      pendingLinkRangeRef.current = null;
    }
  }, []);

  const openInsertLinkModal = () => {
    editingLinkAnchorRef.current = null;
    editingLinkHrefRef.current = "";
    linkModalModeRef.current = "insert";
    linkEditAnchorRef.current = null;
    setLinkModalMode("insert");
    setLinkUrlDraft("");
    setLinkModalOpen(true);
    closeLinkBubble();
  };

  const openEditLinkModal = () => {
    pendingLinkRangeRef.current = null;
    const a = linkEditAnchorRef.current || linkBubble.anchor;
    if (!a) return;
    linkEditAnchorRef.current = a;
    editingLinkAnchorRef.current = a;
    editingLinkHrefRef.current = a.getAttribute("href") || "";
    linkBubbleAnchorRef.current = null;
    setLinkBubble({ open: false, x: 0, y: 0, href: "", anchor: null });
    setLinkModalMode("edit");
    linkModalModeRef.current = "edit";
    setLinkUrlDraft(a.getAttribute("href") || "");
    setLinkModalOpen(true);
    contentEditableRef.current?.focus();
  };

  const applyLinkFromModal = () => {
    const url = normalizeUrlForLink(linkUrlDraft);
    if (!isSafeHref(url)) {
      message.error(
        "Enter a valid URL starting with https:// or http://.",
      );
      return;
    }
    const el = contentEditableRef.current;
    if (!el) return;
    el.focus();

    if (linkModalModeRef.current === "edit") {
      let a = editingLinkAnchorRef.current;
      const oldHref = editingLinkHrefRef.current;
      if ((!a || !el.contains(a)) && oldHref) {
        for (const cand of el.querySelectorAll("a[href]")) {
          if ((cand.getAttribute("href") || "") === oldHref) {
            a = cand;
            break;
          }
        }
      }
      if (!a || !el.contains(a)) {
        message.error("Could not update that link. Close the dialog, select the link, and use Change again.");
        editingLinkAnchorRef.current = null;
        editingLinkHrefRef.current = "";
        linkModalModeRef.current = "insert";
        setLinkModalOpen(false);
        return;
      }
      pendingLinkRangeRef.current = null;
      a.setAttribute("href", url);
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
      editingLinkAnchorRef.current = null;
      editingLinkHrefRef.current = "";
      linkModalModeRef.current = "insert";
      setLinkModalMode("insert");
      setLinkModalOpen(false);
      applySanitizedEditorHtml(el);
      return;
    }

    const pending = pendingLinkRangeRef.current;
    pendingLinkRangeRef.current = null;
    el.focus();

    let range = null;
    if (pending) {
      try {
        if (el.contains(pending.commonAncestorContainer)) {
          range = pending.cloneRange();
        }
      } catch {
        range = null;
      }
    }
    if (!range) {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const r = sel.getRangeAt(0);
        if (el.contains(r.commonAncestorContainer)) range = r;
      }
    }

    if (!range || !el.contains(range.commonAncestorContainer)) {
      message.warning(
        "Select text in the editor first, or place the cursor where you want a link.",
      );
      setLinkModalOpen(false);
      return;
    }

    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
    const live = sel.rangeCount > 0 ? sel.getRangeAt(0) : range;

    if (live.collapsed) {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.textContent = url;
      live.insertNode(a);
      // Insert a zero-width space so cursor lands outside the link
      const zwsp = document.createTextNode("\u200B");
      if (a.nextSibling) {
        a.parentNode.insertBefore(zwsp, a.nextSibling);
      } else {
        a.parentNode.appendChild(zwsp);
      }
      const after = document.createRange();
      after.setStart(zwsp, 1);
      after.collapse(true);
      sel.removeAllRanges();
      sel.addRange(after);
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      try {
        live.surroundContents(a);
      } catch {
        const frag = live.extractContents();
        a.appendChild(frag);
        live.insertNode(a);
      }
      // Insert a zero-width space after the anchor so the cursor
      // can land in a non-link text node and typing continues normally.
      const zwsp = document.createTextNode("\u200B");
      if (a.nextSibling) {
        a.parentNode.insertBefore(zwsp, a.nextSibling);
      } else {
        a.parentNode.appendChild(zwsp);
      }
      sel.removeAllRanges();
      const after = document.createRange();
      after.setStart(zwsp, 1);
      after.collapse(true);
      sel.addRange(after);
    }

    setLinkModalOpen(false);
    applySanitizedEditorHtml(el);
    checkActiveFormats();
  };

  const removeActiveLink = () => {
    const el = contentEditableRef.current;
    if (!el) return;

    let a =
      linkBubbleAnchorRef.current ||
      linkEditAnchorRef.current ||
      linkBubble.anchor;

    if (a && !el.contains(a)) {
      a = null;
    }
    if (!a) {
      const href = linkBubble.href;
      if (href) {
        for (const cand of el.querySelectorAll("a[href]")) {
          if ((cand.getAttribute("href") || "") === href) {
            a = cand;
            break;
          }
        }
      }
    }

    if (!a || !el.contains(a)) return;

    if (editingLinkAnchorRef.current === a) editingLinkAnchorRef.current = null;
    linkBubbleAnchorRef.current = null;
    unwrapAnchor(a);
    closeLinkBubble();
    applySanitizedEditorHtml(el);
    checkActiveFormats();
  };

  const nodeFromEventTarget = (target) =>
    target?.nodeType === Node.TEXT_NODE ? target.parentElement : target;

  const handleEditorMouseDown = (e) => {
    const el = contentEditableRef.current;
    const node = nodeFromEventTarget(e.target);
    const a = node?.closest?.("a");
    if (a && el?.contains(a) && !e.ctrlKey && !e.metaKey && e.button === 0) {
      e.preventDefault();
    }
  };

  const handleEditorMouseMove = (e) => {
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    if (now - linkHoverThrottleRef.current < 40) return;
    linkHoverThrottleRef.current = now;

    const el = contentEditableRef.current;
    if (!el?.contains(e.target)) return;

    const node = nodeFromEventTarget(e.target);
    const anchor = node?.closest?.("a");
    if (anchor && el.contains(anchor)) {
      openLinkBubbleForAnchor(anchor);
    } else if (linkBubbleOpenRef.current) {
      scheduleHideBubble();
    }
  };

  const handleEditorMouseLeave = (e) => {
    if (e.relatedTarget?.closest?.("[data-link-bubble]")) {
      cancelHideBubble();
      return;
    }
    scheduleHideBubble();
  };

  const handleEditorClick = (e) => {
    const el = contentEditableRef.current;
    const target = e.target;
    if (!el?.contains(target)) return;

    const node = nodeFromEventTarget(target);
    const anchor = node?.closest?.("a");
    if (anchor && el.contains(anchor)) {
      cancelHideBubble();
      openLinkBubbleForAnchor(anchor);
    } else {
      closeLinkBubble();
    }
    checkActiveFormats();
  };

  const handleEditorKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
      e.preventDefault();
      capturePendingLinkRange();
      openInsertLinkModal();
    }
  };

  const ToolbarButton = ({ icon, command, arg, title }) => (
    <Tooltip title={title}>
      <Button
        type={activeFormats[command] ? "primary" : "text"}
        icon={icon}
        onClick={(ev) => {
          ev.preventDefault();
          execCommand(command, arg);
        }}
        size="small"
        className="mr-1"
      />
    </Tooltip>
  );

  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain") || "";

    if (html) {
      const safe = sanitizeHtml(html);
      document.execCommand(
        "insertHTML",
        false,
        safe || sanitizeHtml(text.replace(/\n/g, "<br>")),
      );
    } else {
      document.execCommand("insertText", false, text);
    }

    if (contentEditableRef.current) {
      applySanitizedEditorHtml(contentEditableRef.current);
    }
    checkActiveFormats();
  };

  const replaceSelectionWithHtml = useCallback(
    (replacement) => {
      if (!contentEditableRef.current) return;
      contentEditableRef.current.focus();
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      range.deleteContents();

      const fragment = range.createContextualFragment(sanitizeHtml(replacement));
      const lastNode = fragment.lastChild;
      range.insertNode(fragment);

      if (lastNode) {
        range.setStartAfter(lastNode);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
      }

      applySanitizedEditorHtml(contentEditableRef.current);
    },
    [applySanitizedEditorHtml],
  );

  const getEnhanceContext = useCallback(() => {
    const el = contentEditableRef.current;
    if (!el) return null;
    el.focus();
    const sel = window.getSelection();
    let text = sel?.toString()?.trim() || "";
    const hadSelection =
      !!sel &&
      sel.rangeCount > 0 &&
      sel.toString().trim().length > 0 &&
      el.contains(sel.anchorNode);
    let rangeClone = null;
    if (hadSelection) {
      try {
        rangeClone = sel.getRangeAt(0).cloneRange();
      } catch {
        rangeClone = null;
      }
    }
    if (!text) {
      text = el.innerHTML || "";
    }
    return {
      text,
      rangeClone,
      hadRange: !!(hadSelection && rangeClone),
    };
  }, []);

  const onApplyEnhanced = useCallback(
    (enhanced, { rangeClone, hadRange }) => {
      const el = contentEditableRef.current;
      if (!el) return;
      if (hadRange && rangeClone) {
        try {
          const inEditor = el.contains(rangeClone.commonAncestorContainer);
          if (inEditor) {
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(rangeClone);
            replaceSelectionWithHtml(enhanced);
          } else {
            el.innerHTML = sanitizeHtml(enhanced);
            applySanitizedEditorHtml(el);
          }
        } catch {
          el.innerHTML = sanitizeHtml(enhanced);
          applySanitizedEditorHtml(el);
        }
      } else {
        el.innerHTML = sanitizeHtml(enhanced);
        applySanitizedEditorHtml(el);
      }
    },
    [replaceSelectionWithHtml, applySanitizedEditorHtml],
  );

  return (
    <div
      className="border border-gray-300 rounded-md flex flex-col hover:border-blue-400 transition-colors relative"
      onKeyDown={handleEditorKeyDown}
    >
      <div className="flex w-full flex-wrap items-start gap-y-1 p-2 border-b border-gray-200 bg-gray-50 rounded-t-md">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
          <ToolbarButton icon={<BoldOutlined />} command="bold" title="Bold" />
          <ToolbarButton icon={<ItalicOutlined />} command="italic" title="Italic" />
          <ToolbarButton icon={<UnderlineOutlined />} command="underline" title="Underline" />
          <Tooltip title="Insert link (Ctrl+K)">
            <Button
              type="text"
              icon={<LinkOutlined />}
              onMouseDown={(ev) => {
                ev.preventDefault();
                capturePendingLinkRange();
              }}
              onClick={(ev) => {
                ev.preventDefault();
                openInsertLinkModal();
              }}
              size="small"
              className="mr-1"
            />
          </Tooltip>
          <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
          <ToolbarButton icon={<UnorderedListOutlined />} command="insertUnorderedList" title="Bullet List" />
          <ToolbarButton icon={<OrderedListOutlined />} command="insertOrderedList" title="Numbered List" />
          <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
          <ToolbarButton icon={<AlignLeftOutlined />} command="justifyLeft" title="Align Left" />
          <ToolbarButton icon={<AlignCenterOutlined />} command="justifyCenter" title="Align Center" />
          <ToolbarButton icon={<AlignRightOutlined />} command="justifyRight" title="Align Right" />
        </div>
        {showAiEnhance && (
          <AiTextEnhanceControl
            target={aiEnhanceTarget}
            getEnhanceContext={getEnhanceContext}
            onApplyEnhanced={onApplyEnhanced}
          />
        )}
      </div>
      <div
        ref={contentEditableRef}
        className="p-3 min-h-[150px] outline-none max-h-[300px] overflow-y-auto empty:before:content-[attr(placeholder)] empty:before:text-gray-400 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:list-inside [&_a]:text-blue-600 [&_a]:underline"
        contentEditable={true}
        onInput={handleInput}
        onKeyUp={checkActiveFormats}
        onMouseUp={checkActiveFormats}
        onMouseDown={handleEditorMouseDown}
        onMouseMove={handleEditorMouseMove}
        onMouseLeave={handleEditorMouseLeave}
        onClick={handleEditorClick}
        onPaste={handlePaste}
        placeholder={placeholder}
        style={{ minHeight: "150px" }}
      />

      {linkBubble.open && (
        <div
          data-link-bubble
          className="fixed z-[1100] rounded border border-gray-200 bg-white shadow-md py-2 px-3 text-sm max-w-[min(280px,calc(100vw-16px))]"
          style={{ left: linkBubble.x, top: linkBubble.y }}
          onMouseDown={(ev) => {
            if (ev.target.closest?.("button") || ev.target.closest?.("a")) return;
            ev.preventDefault();
          }}
          onMouseEnter={cancelHideBubble}
          onMouseLeave={scheduleHideBubble}
        >
          <div className="mb-2 break-all">
            <span className="text-gray-500">Go to link: </span>
            <a
              href={linkBubble.href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600"
            >
              {linkBubble.href}
            </a>
          </div>
          <div className="flex flex-wrap gap-x-2 gap-y-1">
            <button type="button" className="text-blue-600 hover:underline" onClick={openEditLinkModal}>
              Change
            </button>
            <span className="text-gray-300">|</span>
            <button type="button" className="text-blue-600 hover:underline" onClick={removeActiveLink}>
              Remove
            </button>
          </div>
        </div>
      )}

      <Modal
        title={linkModalMode === "edit" ? "Edit link" : "Insert link"}
        open={linkModalOpen}
        onOk={applyLinkFromModal}
        onCancel={() => {
          pendingLinkRangeRef.current = null;
          editingLinkAnchorRef.current = null;
          editingLinkHrefRef.current = "";
          linkModalModeRef.current = "insert";
          setLinkModalMode("insert");
          setLinkModalOpen(false);
        }}
        okText="Apply"
        destroyOnClose
      >
        <p className="text-gray-500 text-sm mb-2">
          Use a full URL starting with https:// or http://.
        </p>
        <Input
          placeholder="https://example.com"
          value={linkUrlDraft}
          onChange={(ev) => setLinkUrlDraft(ev.target.value)}
          onPressEnter={applyLinkFromModal}
        />
      </Modal>
    </div>
  );
};

export default SimpleRichTextEditor;
